// src/pages/AdminPoolItemsPage.jsx
import React, { useEffect, useState } from "react";
import { Pencil, X, RefreshCcw } from "lucide-react";

export default function AdminPoolItemsPage() {
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filter, setFilter] = useState("");
  const [filterParams, setFilterParams] = useState({});
  const [selected, setSelected] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadItems = async (params = {}) => {
    setLoading(true);
    try {
      const queryString = new URLSearchParams(params).toString();

      const res = await fetch(`/api/poolitems${queryString ? `?${queryString}` : ""}`, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Chyba při načítání");

      const json = await res.json();
      setItems(json);
      setFiltered(json);
      setFilterParams(params);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleFilter = (val) => {
    setFilter(val);
    if (!val.trim()) return setFiltered(items);
    const q = val.toLowerCase();
    const out = items.filter((i) =>
      Object.values(i).some((v) => String(v).toLowerCase().includes(q))
    );
    setFiltered(out);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);

    try {
      const res = await fetch(`/api/poolitems/${selected._id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selected),
      });

      if (!res.ok) throw new Error("Chyba při ukládání");

      await loadItems(filterParams);
      setSelected(null);
    } catch (err) {
      console.error(err);
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Opravdu smazat položku ${selected["Název"]}?`)) return;

    setDeleting(true);
    try {
      const res = await fetch(`/api/poolitems/${selected._id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Chyba při mazání");

      await loadItems(filterParams);
      setSelected(null);
    } catch (err) {
      console.error(err);
    }
    setDeleting(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      const res = await fetch("/api/poolitems/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(filterParams),
      });

      if (!res.ok) throw new Error("Chyba při refreshi");

      const json = await res.json();
      setItems(json);
      setFiltered(json);
    } catch (err) {
      console.error("Chyba při refreshi:", err);
    }

    setRefreshing(false);
  };

  const stavOptions = [
    "aktivní",
    "rezervováno",
    "potvrzeno",
    "vychystáno",
    "odesláno",
    "přijato",
    "vyřešeno",
    "odepsáno",
    "vyřazeno",
  ];

  if (loading) return <div className="p-6 text-center">Načítání...</div>;

  return (
    <div style={{ padding: "20px" }}>
      <h1 style={{ fontSize: "22px", fontWeight: "600", marginBottom: "10px" }}>
        Pool Items (správa)
      </h1>

      {/* horní panel */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
        <input
          type="text"
          placeholder="Hledat podle libovolného pole..."
          value={filter}
          onChange={(e) => handleFilter(e.target.value)}
          style={{
            padding: "6px 10px",
            width: "250px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 14px",
            borderRadius: "6px",
            background: refreshing
              ? "linear-gradient(90deg, #ddd, #bbb)"
              : "linear-gradient(90deg, #007BFF, #00C6FF)",
            color: "white",
            fontWeight: "500",
            border: "none",
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            transition: "0.2s",
          }}
        >
          <RefreshCcw
            size={16}
            style={{
              transform: refreshing ? "rotate(360deg)" : "none",
              transition: "transform 0.6s linear",
            }}
          />
          {refreshing ? "Aktualizuji..." : "Aktualizovat z DB"}
        </button>
      </div>

      {/* tabulka */}
      <div style={{ overflowX: "auto" }}>
        <table border="1" cellPadding="5" cellSpacing="0" width="100%">
          <thead style={{ background: "#f5f5f5" }}>
            <tr>
              <th>PDK</th>
              <th>Název</th>
              <th>Množství</th>
              <th>Exspirace</th>
              <th>Stav</th>
              <th>Pobočka</th>
              <th>Cíl</th>
              <th>Důvod</th>
              <th>Akce</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((item) => (
              <tr key={item._id}>
                <td>{item.PDK}</td>
                <td>{item["Název"]}</td>
                <td>{item.Množství}</td>
                <td>
                  {item.Exspirace
                    ? new Date(item.Exspirace).toLocaleDateString("cs-CZ", {
                        timeZone: "Europe/Prague",
                      })
                    : ""}
                </td>
                <td>{item.stav}</td>
                <td>{item.pharmacyCode}</td>
                <td>{item.targetPharmacyCode || "-"}</td>
                <td>{item.duvod}</td>
                <td>
                  <button
                    onClick={() => setSelected(item)}
                    style={{
                      background: "#eee",
                      border: "1px solid #ccc",
                      padding: "3px 6px",
                      cursor: "pointer",
                    }}
                  >
                    <Pencil size={14} /> Upravit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* modál */}
      {selected && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              padding: "20px",
              width: "850px",
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2>
                Úprava položky {selected.PDK} – {selected["Název"]}
              </h2>
              <button
                onClick={() => setSelected(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                }}
              >
                <X />
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
                marginTop: "15px",
              }}
            >
              {Object.entries(selected).map(([key, val]) => {
                if (["_id", "__v", "history"].includes(key)) return null;

                if (key === "stav") {
                  return (
                    <div key={key}>
                      <label>Stav</label>
                      <select
                        value={selected.stav}
                        onChange={(e) =>
                          setSelected({ ...selected, stav: e.target.value })
                        }
                        style={{ width: "100%", padding: "5px" }}
                      >
                        {stavOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                return (
                  <div key={key}>
                    <label>{key}</label>
                    <input
                      type="text"
                      value={val || ""}
                      onChange={(e) =>
                        setSelected({ ...selected, [key]: e.target.value })
                      }
                      style={{ width: "100%", padding: "5px" }}
                    />
                  </div>
                );
              })}
            </div>

            {selected.history && selected.history.length > 0 && (
              <div style={{ marginTop: "25px" }}>
                <h3>Historie změn</h3>
                <ul>
                  {selected.history.map((h, i) => (
                    <li key={i}>
                      <strong>{h.stav}</strong> –{" "}
                      {new Date(h.changedAt).toLocaleString("cs-CZ", {
                        timeZone: "Europe/Prague",
                      })}{" "}
                      {h.note && <>({h.note})</>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button onClick={() => setSelected(null)}>Zrušit</button>

              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ background: "red", color: "white" }}
              >
                {deleting ? "Mažu..." : "Smazat"}
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                style={{ background: "green", color: "white" }}
              >
                {saving ? "Ukládám..." : "Uložit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
